/* global google */
import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    IconButton,
    Typography,
    Divider,
    Switch,
    FormControlLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { googleMapsConfig } from '../../firebase/config';

const AddOrganizerForm = ({
    open,
    onClose,
    onSubmit,
    newOrganizer,
    handleNewOrganizerChange,
    isEditing
}) => {
    const organizerNameInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [mapsError, setMapsError] = useState(null);
    const [isOnline, setIsOnline] = useState(false);

    // Załaduj Google Maps API
    useEffect(() => {
        if (!window.google) {
            window.REACT_APP_GOOGLE_MAPS_API_KEY = googleMapsConfig.apiKey;
            window.loadGoogleMapsScript();
        }
    }, []);

    // Sprawdź czy Google Maps API jest załadowane
    useEffect(() => {
        const checkGoogleMapsLoaded = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                setMapsLoaded(true);
            } else {
                setTimeout(checkGoogleMapsLoaded, 100);
            }
        };
        checkGoogleMapsLoaded();

        // Nasłuchuj na błędy Google Maps
        window.gm_authFailure = () => {
            setMapsError('Błąd autoryzacji Google Maps API. Sprawdź konfigurację klucza API.');
        };
    }, []);

    // Inicjalizuj autocomplete gdy API jest gotowe
    useEffect(() => {
        if (mapsLoaded && organizerNameInputRef.current && !autocompleteRef.current && !isOnline) {
            try {
                autocompleteRef.current = new google.maps.places.Autocomplete(organizerNameInputRef.current, {
                    types: ['establishment', 'geocode']
                });

                // Dodaj style do pac-container
                const style = document.createElement('style');
                style.textContent = `
                    .pac-container {
                        background-color: rgba(17, 24, 39, 0.95) !important;
                        border: 1px solid rgba(76, 29, 149, 0.2) !important;
                        border-radius: 0.5rem !important;
                        box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37) !important;
                        backdrop-filter: blur(20px) !important;
                        z-index: 9999 !important;
                    }
                    .pac-item {
                        color: white !important;
                        padding: 8px !important;
                        font-size: 14px !important;
                        border-top: 1px solid rgba(76, 29, 149, 0.2) !important;
                    }
                    .pac-item:hover {
                        background-color: rgba(76, 29, 149, 0.2) !important;
                    }
                    .pac-item-query {
                        color: rgb(196, 181, 253) !important;
                    }
                    .pac-icon {
                        filter: invert(1) !important;
                    }
                `;
                document.head.appendChild(style);

                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    if (!place.geometry) return;

                    // Aktualizuj nazwę organizatora
                    handleNewOrganizerChange({
                        target: {
                            name: 'name',
                            value: place.name
                        }
                    });

                    // Aktualizuj adres
                    handleNewOrganizerChange({
                        target: {
                            name: 'address',
                            value: place.formatted_address
                        }
                    });

                    // Aktualizuj współrzędne
                    handleNewOrganizerChange({
                        target: {
                            name: 'coordinates.lat',
                            value: place.geometry.location.lat()
                        }
                    });
                    handleNewOrganizerChange({
                        target: {
                            name: 'coordinates.lng',
                            value: place.geometry.location.lng()
                        }
                    });
                });
            } catch (error) {
                console.error('Błąd podczas inicjalizacji Google Places Autocomplete:', error);
            }
        }
    }, [mapsLoaded, organizerNameInputRef.current, isOnline]);

    const handleOnlineChange = (event) => {
        setIsOnline(event.target.checked);
        if (event.target.checked) {
            // Wyczyść pola lokalizacji dla organizatora online
            handleNewOrganizerChange({
                target: {
                    name: 'address',
                    value: 'Online'
                }
            });
            handleNewOrganizerChange({
                target: {
                    name: 'coordinates.lat',
                    value: ''
                }
            });
            handleNewOrganizerChange({
                target: {
                    name: 'coordinates.lng',
                    value: ''
                }
            });
        }
    };

    const commonTextFieldStyles = {
        '& .MuiOutlinedInput-root': {
            color: 'white',
            backgroundColor: 'rgba(76, 29, 149, 0.1)',
            '& fieldset': {
                borderColor: 'rgb(55, 65, 81)',
            },
            '&:hover fieldset': {
                borderColor: 'rgb(76, 29, 149)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgb(196, 181, 253)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgb(196, 181, 253)',
            '&.Mui-focused': {
                color: 'rgb(196, 181, 253)',
            },
        },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'linear-gradient(145deg, rgba(76, 29, 149, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    borderRadius: '1rem',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(90deg, rgba(76, 29, 149, 0.2) 0%, rgba(76, 29, 149, 0.1) 100%)',
                color: 'white',
                borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                fontWeight: '600',
                padding: '1.25rem',
                backdropFilter: 'blur(10px)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    {isEditing ? 'Edytuj Organizatora' : 'Dodaj Nowego Organizatora'}
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                pt: 2,
                pb: 1,
                background: 'linear-gradient(180deg, rgba(76, 29, 149, 0.05) 0%, rgba(17, 24, 39, 0.95) 100%)'
            }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nazwa organizatora*"
                            name="name"
                            value={newOrganizer.name}
                            onChange={handleNewOrganizerChange}
                            size="small"
                            inputRef={organizerNameInputRef}
                            sx={commonTextFieldStyles}
                            error={!!mapsError}
                            helperText={mapsError || "Wpisz nazwę sklepu lub adres, wybierz z listy..."}
                            autoComplete="off"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Adres*"
                            name="address"
                            value={newOrganizer.address}
                            onChange={handleNewOrganizerChange}
                            size="small"
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Szerokość geograficzna"
                            name="coordinates.lat"
                            type="number"
                            value={newOrganizer.coordinates.lat}
                            onChange={handleNewOrganizerChange}
                            size="small"
                            InputProps={{
                                inputProps: { step: "0.000001" }
                            }}
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Długość geograficzna"
                            name="coordinates.lng"
                            type="number"
                            value={newOrganizer.coordinates.lng}
                            onChange={handleNewOrganizerChange}
                            size="small"
                            InputProps={{
                                inputProps: { step: "0.000001" }
                            }}
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Strona WWW"
                            name="website"
                            value={newOrganizer.website}
                            onChange={handleNewOrganizerChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                background: 'linear-gradient(0deg, rgba(76, 29, 149, 0.1) 0%, rgba(17, 24, 39, 0.95) 100%)',
                borderTop: '1px solid rgba(76, 29, 149, 0.2)',
                p: 2,
                backdropFilter: 'blur(10px)'
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgb(196, 181, 253)',
                        '&:hover': {
                            backgroundColor: 'rgba(76, 29, 149, 0.1)'
                        }
                    }}
                >
                    Anuluj
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    sx={{
                        backgroundColor: 'rgb(76, 29, 149)',
                        '&:hover': {
                            backgroundColor: 'rgb(91, 33, 182)'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(76, 29, 149, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                        }
                    }}
                    disabled={!newOrganizer.name || !newOrganizer.address}
                >
                    {isEditing ? 'Zapisz Zmiany' : 'Zapisz Organizatora'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddOrganizerForm; 